import { ref, computed } from "vue";
import { debounce } from "lodash";

export const resolveHasMore = (result, pageSize) => {
  if (result?.has_more !== undefined) return !!result.has_more;
  const count = result?.items?.length ?? 0;
  return count >= pageSize;
};

export function useSelectInfiniteScroll({
  fetchFn,
  pageSize = 20,
  debounceMs = 300,
}) {
  const items = ref([]);
  const loading = ref(false);
  const loadingMore = ref(false);
  const searchQuery = ref("");
  const total = ref(0);
  const currentPage = ref(1);
  const hasMoreFlag = ref(true);

  const hasMore = computed(() => {
    if (hasMoreFlag.value === false) return false;
    return items.value.length < total.value;
  });

  let fetchId = 0;

  const fetchPage = async (page) => {
    const id = ++fetchId;
    const isFirstPage = page === 1;
    if (isFirstPage) {
      loading.value = true;
    } else {
      loadingMore.value = true;
    }

    try {
      const result = await fetchFn(page, pageSize, searchQuery.value);
      if (id !== fetchId) return;
      const newItems = result?.items || [];
      const newTotal = result?.total ?? 0;

      if (isFirstPage) {
        items.value = newItems;
      } else {
        items.value = [...items.value, ...newItems];
      }
      total.value = newTotal;
      hasMoreFlag.value = resolveHasMore(result, pageSize);
      currentPage.value = page;
    } catch (error) {
      if (id !== fetchId) return;
      console.error("useSelectInfiniteScroll fetch error:", error);
    } finally {
      if (id === fetchId) {
        loading.value = false;
        loadingMore.value = false;
      }
    }
  };

  const loadMore = () => {
    if (!hasMore.value || loading.value || loadingMore.value) return;
    fetchPage(currentPage.value + 1);
  };

  /**
   * Bind to `@popupScroll` on `a-select`.
   * Triggers loading next page when scrolled near the bottom.
   */
  const handlePopupScroll = (event) => {
    const el = event.target;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
    if (nearBottom) {
      loadMore();
    }
  };

  const debouncedFetchFirstPage = debounce(() => {
    fetchPage(1);
  }, debounceMs);

  /**
   * Bind to `@search` on `a-select`.
   * Resets pagination and debounce-fetches page 1 with the new query.
   */
  const handleSearch = (query) => {
    searchQuery.value = query;
    items.value = [];
    total.value = 0;
    currentPage.value = 1;
    loading.value = true;
    debouncedFetchFirstPage();
  };

  /**
   * Call this to trigger the initial data load.
   * Unlike `useInfiniteScroll`, this does NOT auto-fetch on init.
   */
  const fetchFirstPage = () => {
    debouncedFetchFirstPage.cancel();
    items.value = [];
    total.value = 0;
    hasMoreFlag.value = true;
    currentPage.value = 1;
    fetchPage(1);
  };

  const reset = () => {
    debouncedFetchFirstPage.cancel();
    items.value = [];
    total.value = 0;
    hasMoreFlag.value = true;
    currentPage.value = 1;
    searchQuery.value = "";
    loading.value = false;
    loadingMore.value = false;
  };

  return {
    items,
    loading,
    loadingMore,
    searchQuery,
    hasMore,
    total,
    handlePopupScroll,
    handleSearch,
    fetchFirstPage,
    reset,
    loadMore,
  };
}
